import { createWithEqualityFn } from 'zustand/traditional';
import type {
  ModelHistoryCacheEntry,
  ModelHistoryInputSnapshot,
  ModelHistoryOutputSnapshot,
} from '../config/models.config';
import { isFormDataLike } from '../utils/http';

const HISTORY_STORAGE_KEY = 'iarts_history_cache_v1';
const PREFS_STORAGE_KEY = 'iarts_prefs';
const HISTORY_MAX_ITEMS = 200;
const MAX_SERIALIZE_DEPTH = 5;
const MAX_SERIALIZE_ARRAY_LENGTH = 50;

type HistoryEntryStatus = 'pending' | 'success' | 'failed';

export type HistoryStoredValue =
  | string
  | number
  | boolean
  | null
  | HistoryStoredValue[]
  | { [key: string]: HistoryStoredValue };

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const getSafeStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const shouldAutoSaveHistory = (): boolean => {
  const storage = getSafeStorage();
  if (!storage) return true;
  try {
    const raw = storage.getItem(PREFS_STORAGE_KEY);
    if (!raw) {
      return true;
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed && typeof parsed.autoSaveHistory === 'boolean') {
      return parsed.autoSaveHistory;
    }
  } catch {
    // ignore parsing errors and fallback to true
  }
  return true;
};

/** 检测并截断 base64 data URL */
const truncateBase64String = (str: string): string => {
  // 检测 base64 data URL
  if (str.startsWith('data:') && str.includes('base64,') && str.length > 1000) {
    const base64Index = str.indexOf('base64,');
    const mimeMatch = /^data:([^;,]+)/.exec(str);
    const mimeType = mimeMatch?.[1] || 'unknown';
    return `data:${mimeType};base64,[truncated-${Math.round(str.length / 1024)}KB]`;
  }
  // 检测裸 base64 字符串（超过 10KB 的纯 base64）
  if (str.length > 10000 && /^[A-Za-z0-9+/=\s]+$/.test(str.replace(/\s/g, ''))) {
    return `[base64-truncated-${Math.round(str.length / 1024)}KB]`;
  }
  return str;
};

const toStoredValue = (
  value: unknown,
  seen: WeakSet<object>,
  depth: number
): HistoryStoredValue => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    return truncateBase64String(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'symbol') {
    return value.description ? `Symbol(${value.description})` : 'Symbol';
  }
  if (typeof value === 'function') {
    return `[Function ${value.name || 'anonymous'}]`;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? value.toString() : value.toISOString();
  }
  if (typeof URL !== 'undefined' && value instanceof URL) {
    return value.toString();
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    const isFile = typeof File !== 'undefined' && value instanceof File;
    const base: Record<string, HistoryStoredValue> = {
      __type: isFile ? 'File' : 'Blob',
      size: value.size,
    };
    if (value.type) {
      base.type = value.type;
    }
    if (isFile && value.name) {
      base.name = value.name;
    }
    return base;
  }
  if (value instanceof ArrayBuffer) {
    return { __type: 'ArrayBuffer', byteLength: value.byteLength };
  }
  if (ArrayBuffer.isView(value)) {
    const viewInfo: Record<string, HistoryStoredValue> = {
      __type: value.constructor.name,
      byteLength: value.byteLength,
    };
    if ('length' in value && typeof (value as { length?: unknown }).length === 'number') {
      viewInfo.length = (value as { length: number }).length;
    }
    return viewInfo;
  }
  if (isFormDataLike(value)) {
    const entries: HistoryStoredValue[] = [];
    value.forEach((entryValue, key) => {
      entries.push({
        key,
        value:
          typeof entryValue === 'string'
            ? entryValue
            : `[${entryValue && typeof entryValue === 'object' && 'constructor' in entryValue ? (entryValue as { constructor?: { name?: string } }).constructor?.name || 'Object' : 'Unknown'}]`,
      });
    });
    return { __type: 'FormData', entries };
  }
  if (depth >= MAX_SERIALIZE_DEPTH) {
    return '[MaxDepth]';
  }
  if (typeof value === 'object') {
    if (seen.has(value as object)) {
      return '[Circular]';
    }
    seen.add(value as object);
    if (Array.isArray(value)) {
      const next: HistoryStoredValue[] = [];
      const limit = Math.min(value.length, MAX_SERIALIZE_ARRAY_LENGTH);
      for (let i = 0; i < limit; i += 1) {
        next.push(toStoredValue(value[i], seen, depth + 1));
      }
      if (limit < value.length) {
        next.push(`[+${value.length - limit} more]`);
      }
      return next;
    }
    if (value instanceof Map) {
      const entries: HistoryStoredValue[] = [];
      let index = 0;
      value.forEach((mapValue, key) => {
        if (index >= MAX_SERIALIZE_ARRAY_LENGTH) return;
        entries.push({
          key: typeof key === 'string' ? key : JSON.stringify(key),
          value: toStoredValue(mapValue, seen, depth + 1),
        });
        index += 1;
      });
      return { __type: 'Map', entries };
    }
    if (value instanceof Set) {
      const entries: HistoryStoredValue[] = [];
      let index = 0;
      value.forEach((setValue) => {
        if (index >= MAX_SERIALIZE_ARRAY_LENGTH) return;
        entries.push(toStoredValue(setValue, seen, depth + 1));
        index += 1;
      });
      return { __type: 'Set', entries };
    }
    if (isPlainObject(value)) {
      const result: Record<string, HistoryStoredValue> = {};
      Object.entries(value).forEach(([key, entryValue]) => {
        result[key] = toStoredValue(entryValue, seen, depth + 1);
      });
      return result;
    }
    try {
      const jsonValue = JSON.parse(JSON.stringify(value));
      if (jsonValue && typeof jsonValue === 'object') {
        return toStoredValue(jsonValue, seen, depth + 1);
      }
    } catch {
      // ignore JSON stringify failure
    }
    return String(value);
  }
  return String(value);
};

const sanitizeValue = (value: unknown): HistoryStoredValue => toStoredValue(value, new WeakSet<object>(), 0);

const sanitizeParameters = (parameters: Record<string, unknown> | undefined): Record<string, HistoryStoredValue> => {
  if (!parameters || !isPlainObject(parameters)) {
    return {};
  }
  const result: Record<string, HistoryStoredValue> = {};
  Object.entries(parameters).forEach(([key, value]) => {
    result[key] = sanitizeValue(value);
  });
  return result;
};

const stringifyHistoryValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? value.toString() : value.toISOString();
  }
  if (isFormDataLike(value)) {
    const entries: string[] = [];
    value.forEach((entryValue, key) => {
      const normalized =
        typeof entryValue === 'string'
          ? entryValue
          : `[${entryValue && typeof entryValue === 'object' && 'constructor' in entryValue ? (entryValue as { constructor?: { name?: string } }).constructor?.name || 'Object' : 'Unknown'}]`;
      entries.push(`${key}: ${normalized}`);
    });
    return entries.length > 0 ? entries.join('\n') : '[FormData]';
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    const isFile = typeof File !== 'undefined' && value instanceof File;
    const label = isFile && value.name ? value.name : `size=${value.size}`;
    return `[${isFile ? 'File' : 'Blob'} ${label}]`;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'unknown error';
      return `[Unserializable: ${reason}]`;
    }
  }
  if (typeof value === 'symbol') {
    return value.description ? `Symbol(${value.description})` : 'Symbol';
  }
  return String(value);
};

const buildInputPreview = (input: ModelHistoryInputSnapshot): string | null => {
  if (input.prompt && input.prompt.trim()) {
    return input.prompt;
  }
  if (input.parameters && Object.keys(input.parameters).length > 0) {
    try {
      return JSON.stringify(input.parameters, null, 2);
    } catch {
      return '[parameters]';
    }
  }
  return null;
};

const buildOutputPreview = (output?: ModelHistoryOutputSnapshot): string | null => {
  if (!output) return null;
  return stringifyHistoryValue(output.rawResponse);
};

const persistHistory = (items: HistoryItem[]) => {
  const storage = getSafeStorage();
  if (!storage) return;
  if (!shouldAutoSaveHistory()) return;
  try {
    storage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.warn('persistHistory: failed to persist history items', error);
  }
};

const loadHistoryFromStorage = (): HistoryItem[] => {
  const storage = getSafeStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as HistoryItem[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry): entry is HistoryItem => entry && typeof entry === 'object' && 'id' in entry)
      .map((entry) => ({
        ...entry,
        cache: {
          modelKey: entry.cache?.modelKey ?? entry.model,
          input: entry.cache?.input ?? {
            prompt: '',
            parameters: {},
            createdAt: new Date(entry.createdAt).toISOString(),
          },
          output: entry.cache?.output,
        },
        status: entry.status ?? 'success',
      }));
  } catch (error) {
    console.warn('loadHistoryFromStorage: failed to parse history cache', error);
    return [];
  }
};

export interface HistoryItem {
  id: string;
  model: string;
  createdAt: number;
  status: HistoryEntryStatus;
  input: string | null;
  output?: string | null;
  cache: ModelHistoryCacheEntry;
  requestId?: string;
  error?: string | null;
}

export interface CreateHistoryEntryOptions {
  id?: string;
  status?: HistoryEntryStatus;
  requestId?: string;
}

export interface UpdateHistoryEntryPayload {
  model?: string;
  input?: ModelHistoryInputSnapshot;
  output?: ModelHistoryOutputSnapshot | null;
  status?: HistoryEntryStatus;
  requestId?: string;
  error?: string | null;
}

interface HistoryState {
  items: HistoryItem[];
  createEntry: (
    model: string,
    input: ModelHistoryInputSnapshot,
    options?: CreateHistoryEntryOptions
  ) => HistoryItem;
  updateEntry: (id: string, payload: UpdateHistoryEntryPayload) => void;
  removeItem: (id: string) => void;
  clear: () => void;
  hydrateFromCache: () => void;
}

const initialItems = loadHistoryFromStorage();

export const useHistoryStore = createWithEqualityFn<HistoryState>()((set) => ({
  items: initialItems,

  createEntry: (model, input, options) => {
    const id = options?.id ?? (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    const createdAt = Date.now();
    const sanitizedInput: ModelHistoryInputSnapshot = {
      prompt: input.prompt ?? '',
      parameters: sanitizeParameters(input.parameters),
      createdAt: input.createdAt ?? new Date(createdAt).toISOString(),
    };
    const cache: ModelHistoryCacheEntry = {
      modelKey: model,
      input: sanitizedInput,
      output: undefined,
    };
    const item: HistoryItem = {
      id,
      model,
      createdAt,
      status: options?.status ?? 'pending',
      input: buildInputPreview(sanitizedInput),
      output: undefined,
      cache,
      requestId: options?.requestId,
      error: null,
    };

    set((state) => {
      const nextItems = [item, ...state.items].slice(0, HISTORY_MAX_ITEMS);
      persistHistory(nextItems);
      return { items: nextItems };
    });
    return item;
  },

  updateEntry: (id, payload) => {
    set((state) => {
      const nextItems = state.items.map((existing) => {
        if (existing.id !== id) return existing;

        const updatedCache: ModelHistoryCacheEntry = {
          modelKey: payload.model ?? existing.cache.modelKey,
          input:
            payload.input
              ? {
                  prompt: payload.input.prompt ?? existing.cache.input.prompt ?? '',
                  parameters: sanitizeParameters(payload.input.parameters),
                  createdAt: payload.input.createdAt ?? existing.cache.input.createdAt,
                }
              : existing.cache.input,
          output:
            payload.output === null
              ? undefined
              : payload.output
              ? { rawResponse: sanitizeValue(payload.output.rawResponse) }
              : existing.cache.output,
        };

        const nextOutput =
          payload.output === null
            ? undefined
            : payload.output
            ? buildOutputPreview(updatedCache.output)
            : existing.output;

        const nextInputPreview = payload.input ? buildInputPreview(updatedCache.input) : existing.input;

        const updated: HistoryItem = {
          ...existing,
          model: payload.model ?? existing.model,
          cache: updatedCache,
          input: nextInputPreview,
          output: nextOutput,
          status: payload.status ?? existing.status,
          requestId: payload.requestId ?? existing.requestId,
          error: payload.error ?? existing.error,
        };

        if (payload.output && !payload.status) {
          updated.status = 'success';
        }
        if (payload.error && !payload.output) {
          updated.status = 'failed';
          updated.output = payload.error;
          updated.cache.output = {
            rawResponse: payload.error,
          };
        }

        return updated;
      });
      persistHistory(nextItems);
      return { items: nextItems };
    });
  },

  removeItem: (id) =>
    set((state) => {
      const nextItems = state.items.filter((item) => item.id !== id);
      persistHistory(nextItems);
      return { items: nextItems };
    }),

  clear: () =>
    set(() => {
      persistHistory([]);
      return { items: [] };
    }),

  hydrateFromCache: () => {
    const items = loadHistoryFromStorage();
    set({ items });
  },
}));
